package com.trevorwiebe.timesheet.admin.usermanagement.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.ownerOperator.domain.OwnerOperatorRepository
import com.trevorwiebe.timesheet.admin.usermanagement.domain.UserManagementRepository
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.OwnerOperator
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class UserManagementViewModel(
    private val repository: UserManagementRepository,
    private val coreRepository: CoreRepository,
    private val ownerOperatorRepository: OwnerOperatorRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(UserManagementState())
    val state = _state.asStateFlow()

    init {
        loadCurrentUserId()
        loadUsers()
        loadOwnerOperators()
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadOwnerOperators() {
        viewModelScope.launch {
            val flow = ownerOperatorRepository.getOwnerOperators()
            flow.collect { result ->
                if (result.error == null) {
                    val ownerOperators = (result.data as List<OwnerOperator>)
                        .filter { it.active && !it.deleted }
                    _state.update { it.copy(ownerOperators = ownerOperators) }
                }
            }
        }
    }

    private fun loadCurrentUserId() {
        viewModelScope.launch {
            val result = coreRepository.getUserId()
            if (result.error == null) {
                _state.update { it.copy(currentUserId = result.data as? String) }
            }
        }
    }

    fun onEvent(event: UserManagementEvent) {
        when (event) {
            is UserManagementEvent.OnAddUserClick ->
                _state.update { it.copy(showAddEditDialog = true, editingUser = null) }

            is UserManagementEvent.OnEditUserClick ->
                _state.update {
                    it.copy(
                        showAddEditDialog = true,
                        editingUser = event.user,
                        passwordResetEmailSent = false,
                    )
                }

            is UserManagementEvent.OnDismissDialog ->
                _state.update {
                    it.copy(
                        showAddEditDialog = false,
                        editingUser = null,
                        passwordResetEmailSent = false,
                    )
                }

            is UserManagementEvent.OnCreateUser -> createUser(event)
            is UserManagementEvent.OnUpdateUser -> updateUser(event)
            is UserManagementEvent.OnSendPasswordResetEmail -> sendPasswordResetEmail()
            is UserManagementEvent.OnDeleteUser -> setDeleted(true)
            is UserManagementEvent.OnRestoreUser -> setDeleted(false)

            is UserManagementEvent.OnShowDeletedToggled ->
                _state.update { it.copy(showDeleted = event.show) }

            is UserManagementEvent.OnErrorShown ->
                _state.update { it.copy(error = null) }
        }
    }

    private fun loadUsers() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            val result = coreRepository.getUserList(includeDeleted = true)
            if (result.error != null) {
                _state.update { it.copy(isLoading = false, error = result.error) }
            } else {
                @Suppress("UNCHECKED_CAST")
                val users = result.data as List<DatabaseUserModel>
                _state.update {
                    it.copy(
                        isLoading = false,
                        users = users.sortedBy { u -> u.displayName.lowercase() },
                        error = null,
                    )
                }
            }
        }
    }

    private fun createUser(event: UserManagementEvent.OnCreateUser) {
        viewModelScope.launch {
            _state.update { it.copy(isSaving = true) }
            val result = repository.createUser(
                name = event.name,
                email = event.email,
                payEntryId = event.payEntryId,
                nickname = event.nickname,
                hireDate = event.hireDate,
                ptoBalance = event.ptoBalance,
                fullTime = event.fullTime,
                holidayTime = event.holidayTime,
                role = event.role,
                ownerOperatorId = event.ownerOperatorId,
            )
            finishSave(result.error)
        }
    }

    private fun updateUser(event: UserManagementEvent.OnUpdateUser) {
        val editing = _state.value.editingUser ?: return
        viewModelScope.launch {
            _state.update { it.copy(isSaving = true) }
            val updated = editing.copy(
                name = event.name,
                email = event.email,
                payEntryId = event.payEntryId,
                nickname = event.nickname,
                hireDate = event.hireDate,
                ptoBalance = event.ptoBalance,
                fullTime = event.fullTime,
                holidayTime = event.holidayTime,
                role = event.role,
                ownerOperatorId = event.ownerOperatorId,
            )
            val result = repository.updateUser(updated)
            finishSave(result.error)
        }
    }

    private fun sendPasswordResetEmail() {
        val editing = _state.value.editingUser ?: return
        viewModelScope.launch {
            _state.update { it.copy(isSendingPasswordReset = true) }
            val result = repository.sendPasswordResetEmail(editing.email)
            _state.update {
                it.copy(
                    isSendingPasswordReset = false,
                    passwordResetEmailSent = result.error == null,
                    error = result.error,
                )
            }
        }
    }

    private fun setDeleted(deleted: Boolean) {
        val editing = _state.value.editingUser ?: return
        viewModelScope.launch {
            _state.update { it.copy(isSaving = true) }
            val result = repository.setUserDeleted(editing, deleted)
            finishSave(result.error)
        }
    }

    private fun finishSave(error: String?) {
        if (error != null) {
            _state.update { it.copy(isSaving = false, error = error) }
        } else {
            _state.update {
                it.copy(isSaving = false, showAddEditDialog = false, editingUser = null)
            }
            loadUsers()
        }
    }
}
